import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichEditor, htmlToText } from "@/components/RichEditor";
import { MailPreviewButton } from "@/components/MailPreview";
import { MailJobProgress, notifyMailJobs } from "@/components/MailJobProgress";
import { SchedulePicker, scheduleIso, emptySchedule, fmtWhen } from "@/components/SchedulePicker";

export const CUSTOMER_PLACEHOLDERS = ["briefanrede", "anrede", "vorname", "nachname", "ansprechpartner", "firma",
  "anzeigenformat", "anzeigengroesse", "gueltig_bis", "ausgabe", "redaktionsschluss", "redaktionsschluss_tag", "anzeigenschluss", "upload_link", "upload_button", "buchungs_button", "abmelde_link", "magazin", "absender"];

function Result({ result }) {
  return (
    <div className="space-y-2 text-sm" data-testid="bulk-mail-result">
      <div className="text-emerald-700 font-medium">{result.sent.length} Mail(s) gesendet</div>
      {result.skipped.length > 0 && <div className="text-amber-800"><b>Übersprungen:</b> {result.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}</div>}
      {result.failed.length > 0 && <div className="text-red-700"><b>Fehlgeschlagen:</b> {result.failed.map((s) => `${s.name} (${s.reason})`).join(", ")}</div>}
    </div>
  );
}

export function CustomerMailDialog({ customer, customers, onClose }) {
  const list = customers || (customer ? [customer] : []);
  const isOpen = list.length > 0;
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("__free__");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const [plan, setPlan] = useState(emptySchedule());
  const [freeRef, setFreeRef] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject(""); setBody(""); setResult(null); setJob(null); setPlan(emptySchedule()); setFreeRef(false);
      api.get("/email-templates").then((r) => {
        const own = r.data.filter((t) => !t.system);
        setTemplates(own); setTpl("__free__");
      }).catch(() => {});
    }
  }, [isOpen]);

  const free = tpl === "__free__";
  const freeAdOnly = free ? freeRef : !!templates.find((x) => x.id === tpl)?.free_ad_only;
  const lacking = freeAdOnly ? list.filter((c) => c.type !== "partner" || !c.ad_format_id) : [];
  const content = { ...(free ? { subject, body } : { template_id: tpl }), free_ad_only: freeAdOnly };
  const when = scheduleIso(plan);
  const ready = (free ? !!subject.trim() && !!htmlToText(body).trim() : !!tpl) && lacking.length === 0 && (!plan.on || (!!when && new Date(when) > new Date()));
  const multi = list.length > 1;
  const editTemplate = () => {
    const t = templates.find((x) => x.id === tpl);
    if (!t) return;
    setSubject(t.subject); setBody(t.body); setFreeRef(!!t.free_ad_only); setTpl("__free__");
  };
  const [lastTpl, setLastTpl] = useState("");
  const showTemplates = () => setTpl(lastTpl || templates[0]?.id || "__free__");

  const send = async () => {
    if ((multi || when) && !window.confirm(when ? `${list.length} Mail(s) für ${fmtWhen(when)} einplanen?` : `Jetzt ${list.length} persönliche Mails versenden?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/customers/mail", { customer_ids: list.map((c) => c.id), ...content, scheduled_at: when });
      if (data.job_id) { setJob(data.job_id); notifyMailJobs(); return; }
      if (!multi && data.sent.length === 1) { toast.success(`E-Mail an ${list[0].name} gesendet`); onClose(true); return; }
      if (!multi) { toast.error([...data.skipped, ...data.failed][0]?.reason || "Nicht gesendet"); return; }
      setResult(data);
      toast.success(`${data.sent.length} von ${list.length} Mails gesendet`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) { onClose(!!result || !!job); } }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="send-mail-dialog">
        <DialogHeader>
          <DialogTitle>{multi ? `E-Mail an ${list.length} Empfänger` : `E-Mail an ${list[0]?.name || ""}`}</DialogTitle>
          <DialogDescription>
            {multi ? `Jeder Empfänger erhält eine eigene, persönlich angesprochene Mail: ${list.map((c) => c.name).join(", ")}` : `Empfänger: ${list[0]?.email || ""}`} · Versand über Dein Microsoft-365-Postfach{multi ? " im Hintergrund (ca. 20 Mails pro Minute)" : ""}.
          </DialogDescription>
        </DialogHeader>
        {job ? <MailJobProgress jobId={job} onJob={setJob} /> : result ? <Result result={result} /> : (
          <div className="space-y-3">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
              {[["free", "Eigene Mail schreiben"], ["tpl", "Vorlage verwenden"]].map(([k, l]) => (
                <button key={k} type="button" disabled={k === "tpl" && templates.length === 0}
                  onClick={() => { if (k === "free") { if (!free) setLastTpl(tpl); setTpl("__free__"); } else { showTemplates(); } }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-40 ${(k === "free") === free ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`mail-mode-${k}`}>{l}</button>
              ))}
            </div>
            {!free && (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label>Vorlage</Label>
                  <Select value={tpl} onValueChange={setTpl}>
                    <SelectTrigger className="mt-1" data-testid="mail-template-select"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" onClick={editTemplate} data-testid="mail-template-edit">Im Editor anpassen</Button>
              </div>
            )}
            {free && (
              <>
                <div><Label>Betreff</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="mail-subject-input" /></div>
                <div><Label>Text</Label><RichEditor value={body} onChange={setBody} placeholders={CUSTOMER_PLACEHOLDERS} testid="mail-body-editor" /></div>
                <p className="text-xs text-slate-500">Platzhalter wie {"{{briefanrede}}"} oder {"{{redaktionsschluss_tag}}"} werden für jeden Empfänger persönlich ersetzt.</p>
              </>
            )}
            {lacking.length > 0 && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mail-free-ad-block">
              Diese Mail betrifft die Freianzeige. {lacking.length === 1 ? "Dieser Empfänger erhält" : "Diese Empfänger erhalten"} keine Freianzeige: <b>{lacking.map((c) => c.name).join(", ")}</b>. Versand gesperrt – bitte in der Liste zuerst den Filter „Mit Freianzeige“ setzen.
            </div>}
            {multi && <p className="text-xs text-slate-500">Die Vorschau zeigt die Mail für {list[0]?.name}. Abgelaufene Partner und Kontakte ohne E-Mail werden übersprungen.</p>}
            <SchedulePicker value={plan} onChange={setPlan} />
          </div>
        )}
        <DialogFooter className="gap-2">
          {result || job ? <Button onClick={() => onClose(true)} data-testid="bulk-mail-close">Schließen</Button> : <>
            <Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
            <MailPreviewButton payload={{ ...content, customer_id: list[0]?.id }} disabled={!ready} testid="customer-mail-preview" />
            <Button onClick={send} disabled={sending || !ready} className="bg-blue-600 hover:bg-blue-700" data-testid="mail-send-button">
              {sending ? "Senden…" : when ? "Versand einplanen" : multi ? `An ${list.length} senden` : "Jetzt senden"}
            </Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
